<template>
  <div
    class="inline-block max-w-full space-y-3"
    :style="headerImageDisplayWidth ? { width: `${headerImageDisplayWidth}px` } : undefined"
  >
    <div
      class="flex items-center gap-2 text-xs font-medium pb-2 border-b"
      :class="
        isSent
          ? 'text-[var(--p-surface-50)] dark:text-[var(--p-primary-100)] border-white/30 dark:border-[var(--p-primary-100)]/20'
          : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)] border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)]'
      "
    >
      <i class="pi pi-file-edit text-xs"></i>
      <span>Plantilla</span>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center gap-2 text-xs opacity-80"
      :class="
        isSent
          ? 'text-[var(--p-surface-50)] dark:text-[var(--p-primary-100)]'
          : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]'
      "
    >
      <i class="pi pi-spin pi-spinner text-xs"></i>
      <span>Cargando plantilla...</span>
    </div>

    <div v-else-if="template" class="min-w-0 space-y-2">
      <div
        v-if="headerFormat === 'IMAGE' && headerContent"
        class="max-w-full overflow-hidden rounded-lg shadow-sm"
        :class="
          isTallHeaderImage ? 'h-[clamp(220px,48vh,360px)] w-[min(65vw,280px)]' : 'w-fit'
        "
      >
        <img
          ref="headerImageElement"
          :src="headerContent"
          :alt="`Imagen de la plantilla ${template.template_name || template.content.name}`"
          class="block"
          :class="
            isTallHeaderImage
              ? 'h-full w-full object-cover'
              : 'h-auto w-auto max-h-[min(48vh,360px)] max-w-[min(65vw,380px)] object-contain'
          "
          @load="handleHeaderImageLoad"
        />
      </div>

      <video
        v-else-if="headerFormat === 'VIDEO' && headerContent"
        :src="headerContent"
        controls
        class="h-[clamp(160px,45vw,250px)] w-[clamp(200px,65vw,380px)] max-w-full rounded-md border border-black/10 bg-black object-cover dark:border-white/10"
      />

      <a
        v-else-if="headerFormat === 'DOCUMENT' && headerContent"
        :href="headerContent"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium underline"
        :class="
          isSent
            ? 'border-white/30 text-[var(--p-surface-0)] dark:border-[var(--p-primary-100)]/20 dark:text-[var(--p-primary-50)]'
            : 'border-[var(--p-surface-200)] text-[var(--p-surface-900)] dark:border-[var(--p-surface-700)] dark:text-[var(--p-surface-0)]'
        "
      >
        <i class="pi pi-file"></i>
        <span class="truncate">Ver documento adjunto</span>
      </a>

      <div
        v-else-if="headerContent"
        class="text-xs font-semibold truncate"
        :class="
          isSent
            ? 'text-[var(--p-surface-0)] dark:text-[var(--p-primary-50)]'
            : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
        "
        v-html="formattedHeaderContent"
      />

      <div
        class="w-full min-w-0 break-words text-[13px] whitespace-pre-wrap"
        :class="
          isSent
            ? 'text-[var(--p-surface-50)] dark:text-[var(--p-primary-50)]'
            : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
        "
        v-html="formattedBodyContent"
      />

      <div
        v-if="template.content.footer"
        class="text-xs opacity-80"
        :class="
          isSent
            ? 'text-[var(--p-surface-50)] dark:text-[var(--p-primary-50)]'
            : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]'
        "
        v-html="formattedFooterContent"
      />

      <div
        v-if="template.content.buttons && template.content.buttons.length > 0"
        class="flex flex-col gap-1.5 mt-1 pt-2 border-t"
        :class="
          isSent
            ? 'border-white/30 dark:border-[var(--p-primary-100)]/20'
            : 'border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)]'
        "
      >
        <div
          v-for="(button, index) in template.content.buttons"
          :key="index"
          class="flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-center border"
          :class="
            isSent
              ? 'bg-white/20 dark:bg-[var(--p-primary-100)]/15 border-white/30 dark:border-[var(--p-primary-100)]/20 text-white dark:text-[var(--p-primary-50)]'
              : 'bg-[var(--p-surface-100)] dark:bg-[var(--p-surface-900)] border-[var(--p-surface-300)] dark:border-[var(--p-surface-600)] text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
          "
        >
          <component :is="getButtonIcon(button.type)" :size="14" :stroke-width="2" />
          <span v-html="formatWhatsappText(button.text)" />
        </div>
      </div>
    </div>

    <div
      v-else-if="templateData"
      class="text-sm"
      :class="
        isSent
          ? 'text-[var(--p-surface-0)] dark:text-[var(--p-primary-50)]'
          : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
      "
    >
      <div class="font-medium mb-1">{{ templateData.template_name }}</div>
      <div v-if="templateData.params && templateData.params.length > 0" class="text-xs space-y-0.5">
        <div v-for="(param, index) in templateData.params" :key="index" class="flex gap-1">
          <span class="opacity-70">Parámetro {{ index + 1 }}:</span>
          <span>{{ param }}</span>
        </div>
      </div>
    </div>

    <div
      v-else
      class="text-xs italic"
      :class="
        isSent
          ? 'text-[var(--p-surface-50)]/75 dark:text-[var(--p-primary-100)]/75'
          : 'text-[var(--p-surface-600)] dark:text-[var(--p-surface-400)]'
      "
    >
      Plantilla sin información
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useTemplateService } from '../../services/useTemplateService'
import type { Template } from '../../types/interfaces'
import { CornerDownRight, ExternalLink, Phone, Volume2 } from 'lucide-vue-next'
import { formatWhatsappText } from '@/utils/whatsappFormatting'

interface Props {
  content: string
  isSent: boolean
}

const props = defineProps<Props>()

interface TemplateData {
  template_id: string
  template_name: string
  params: string[]
}

const { fetchTemplateById } = useTemplateService()
const template = ref<Template | null>(null)
const isLoading = ref(false)
const headerImageElement = ref<HTMLImageElement>()
const headerImageDisplayWidth = ref<number>()
const isTallHeaderImage = ref(false)
let headerImageResizeObserver: ResizeObserver | undefined

const syncHeaderImageDisplayWidth = () => {
  const width = headerImageElement.value?.getBoundingClientRect().width ?? 0
  headerImageDisplayWidth.value = width > 0 ? Math.ceil(width) : undefined
}

const observeHeaderImageWidth = () => {
  headerImageResizeObserver?.disconnect()
  if (!headerImageElement.value || typeof ResizeObserver === 'undefined') return

  headerImageResizeObserver = new ResizeObserver(syncHeaderImageDisplayWidth)
  headerImageResizeObserver.observe(headerImageElement.value)
}

const handleHeaderImageLoad = (event: Event) => {
  const image = event.target as HTMLImageElement
  isTallHeaderImage.value = image.naturalHeight / image.naturalWidth >= 1.35
  nextTick(() => {
    syncHeaderImageDisplayWidth()
    observeHeaderImageWidth()
  })
}

const templateData = computed<TemplateData | null>(() => {
  try {
    return JSON.parse(props.content)
  } catch {
    return null
  }
})

const replaceParams = (text: string, params: string[]): string => {
  let result = text
  params.forEach((param, index) => {
    result = result.replace(`{{${index + 1}}}`, param)
  })
  return result
}

const headerContent = computed(() => {
  if (!template.value?.content.header) return ''

  return replaceParams(
    template.value.content.header.content || '',
    templateData.value?.params || [],
  )
})

const formattedHeaderContent = computed(() => formatWhatsappText(headerContent.value))
const formattedBodyContent = computed(() => {
  if (!template.value) return ''

  return formatWhatsappText(replaceParams(template.value.content.body, templateData.value?.params || []))
})
const formattedFooterContent = computed(() =>
  formatWhatsappText(template.value?.content.footer || ''),
)

const headerFormat = computed(() =>
  String(template.value?.content.header?.format || 'TEXT').toUpperCase(),
)

watch(headerContent, () => {
  isTallHeaderImage.value = false
  headerImageDisplayWidth.value = undefined
})

onBeforeUnmount(() => headerImageResizeObserver?.disconnect())

const getButtonIcon = (buttonType: string) => {
  switch (buttonType) {
    case 'QUICK_REPLY':
      return CornerDownRight
    case 'URL':
      return ExternalLink
    case 'PHONE_NUMBER':
      return Phone
    case 'VOICE_CALL':
      return Volume2
    default:
      return CornerDownRight
  }
}

onMounted(async () => {
  if (templateData.value?.template_id) {
    isLoading.value = true
    try {
      template.value = await fetchTemplateById(templateData.value.template_id)
    } finally {
      isLoading.value = false
    }
  }
})
</script>
